import IEnumerationItem from '../../../../shared/interfaces/IEnumerationItem';
import { LocalDateTime } from '../../../localDate/DateTimeView';

interface ICommon {
    id: number;
}

export enum OrderTypes {
    photo = 1 << 0,
    plan = 1 << 1,
    bookable = 1 << 2,
    styling = 1 << 3,
    showcase = 1 << 4,
    digitalStyling = 1 << 5
}

export enum PhotoOrPlan {
    Unknown = 0,
    Photo = 1,
    Plan = 2,
    None = 3
}

export enum BookableOrderTypes {
    Unknown = 0,
    Photo = 1,
    Styling = 2
}

export interface IOrderType {
    value: number;
    displayName: string;
    cssClass: string;
}

export interface IBasicOrderInfo {
    orderType: OrderTypes;
    createdAt: LocalDateTime;
    startDate?: LocalDateTime;
    orderRows: IOrderRow[];
    customer: ICustomer;
    bookingInfo: IBookingInfo;
    planInfo: IPlanInfo;
    preventSystemEmailsToBroker: boolean;
    preventSystemEmailsToPhotographer: boolean;
    preventCommunicationToPropertyOwner: boolean;
    possibleProductsForUpdate: any;
    ownerCommunication: IOwnerCommunicationViewModel;
    availableImageEditorFirms: ISelectViewModel[];
    availableImageEditors: IImageEditorViewModel[];
    extraDeliveryRecipients: string;
    orderState: IEnumerationViewModel;
    contentProvider: string;
    objectId: string;
    objectNumber: string;
    site: number;
    digitalStylingInfo: { delivered: string };
    brokerAgentEmail: string;
}

export interface ISelectViewModel {
    id: number;
    name: string;
    isDiscontinued: boolean;
}

export interface IImageEditorViewModel {
    id: number;
    name: string;
    imageEditorFirmId: number;
}

export interface IOwnerCommunicationViewModel {
    availableLanguages: ISelectLanguageViewModel[];
    selectedLanguage: string;
}

export interface ISelectLanguageViewModel {
    id: string;
    displayName: string;
}

export interface IEnumerationViewModel {
    value: number;
    displayName: string;
}

interface IArchiveSampleImage {
    filename: string;
    reference: string;
}

export interface IBookingInfo {
    preferredTimeOfPhotographing: LocalDateTime;
    preferredTimeOfPhotographingSlot: string;
    bookedTime: LocalDateTime;
    calculatedEndTime: LocalDateTime;
    completedTime: LocalDateTime;
    calendarItemId: number;
    photographerName: string;
    photographerId: number;
    photoStatus: IEnumerationItem;
    stylingUserName: string;
    stylingStatus: IEnumerationItem;
    bookingType: IEnumerationItem;
    bookableOrderType: IEnumerationItem;
    notes: string;
    keyPickupAddress: string;
    keyPickupZip: string;
    keyPickupCity: string;
    commentsToPhotographer: string;
    commentsToPhotographerFromAgent: string;
    delivered: LocalDateTime;
    isArchived: boolean;
    canRestoreArchivedOrder: boolean;
    archiveStatus: any;
    unarchiveStatus: any;
    dateArchived: LocalDateTime;
    archiveSampleImages: IArchiveSampleImage[];
    restoredDate: LocalDateTime;
    rearchiveDate: LocalDateTime;
    bookingCondition: IEnumerationItem;
    isDirectDelivery: boolean;
    imageEditorFirmId?: number;
    imageEditorId?: number;
    willOrderCreatorAttendWhenPhotographing: boolean;
    hasBookingRequest: boolean;
    canBeMarkedAsHandled: boolean;
    customerWantsClient: boolean;
    numberOfImages: number;
    photoBookingDecision: IBookingDecision;
    stylingBookingDecision: IBookingDecision;
}

export interface IBookingDecision {
    canBook: boolean;
    isBookingLocked: boolean;
    bookingPath: string;
    canCancel: boolean;
}

export const NoBookingDecision: IBookingDecision = {
    canBook: false,
    isBookingLocked: false,
    bookingPath: '',
    canCancel: false
};

export interface IPlanInfo {
    sketchUpStatus: IEnumerationViewModel;
    roomSketcherStatus: IEnumerationViewModel;
    combinedStatus: IEnumerationViewModel;
    dimensions: string;
    delivered: LocalDateTime;
    isArchived: boolean;
    canRestoreArchivedOrder: boolean;
    archiveStatus: any;
    unarchiveStatus: any;
    dateArchived: LocalDateTime;
    restoredDate: LocalDateTime;
    rearchiveDate: LocalDateTime;
    customerDeliveryAnnotation: boolean;
    cubiCasaStatus: IEnumerationViewModel;
}

export interface IProduct extends ICommon {
    name: string;
    priceBrokerFirm: number;
    photographerCompensation: number;
    pricePropertyOwner: number;
    productTypeId: number;
    photoOrPlan: PhotoOrPlan;
    standardProductName: string;
    hasKeyPickup?: boolean;
}

export interface IOrderRow extends ICommon {
    productId: number;
    productName: string;
    productPriceBrokerFirm: number;
    productPricePropertyOwner: number;
    photographerCompensation: number;
    photographerCompensationSetManually: boolean;
    quantity: number;
    invoiceComment: string;
    photographerComment: string;
    removed: boolean;
    productTypeId: number;
    manuallySetCompensationBackup: number;
    photoOrPlan: PhotoOrPlan;
    invoiceStatus: { value: number; displayName: string };
    canChange: boolean;
    canRemove: boolean;
    imageEditingFirmId?: number;
    changeStatus: { value: number; displayName: string };
    useBrokerFirmAddress?: boolean;
    keyPickupAddress?: string;
    keyPickupZip?: string;
    keyPickupCity?: string;
}

export interface IImageProcessingComments {
    internalComment: string;
    originalInternalComment: string;
    editInternalComment: boolean;
}

export interface ICustomer {
    firmName: string;
    firmBrokerTreeNodeId: number;
    agentName: string;
    agentId: number;
    agentBrokerTreeNodeId: number;
    possibleProducts: IProduct[];
    visitorAddress?: string;
    brokerFirmContentProvider: string;
    brokerFirmContentProviderId: number;
}

export interface ISms extends ICommon {
    body: string;
    createdTime: LocalDateTime;
    sentTime: LocalDateTime;
    failedTime: LocalDateTime;
    from: string;
    recipients: string[];
    recipientType: IEnumerationItem;
    desiredSendTime: LocalDateTime;
}

export interface IEmail extends ICommon {
    body: string;
    createdTime: LocalDateTime;
    sentTime: LocalDateTime;
    failedTime: LocalDateTime;
    failed: boolean;
    from: string;
    recipients: string[];
    desiredSendTime: LocalDateTime;
    subject: string;
    isHtml: boolean;
    toRecipients: any[];
    ccRecipients: any[];
    bccRecipients: any[];
    emailAttachmentViewModel: any[];
    hasFile: boolean;
}

export interface IEmailSender {
    id: number;
    displayNameTranslationKey: string;
    emailAddresses: any[];
    defaultForOrder: boolean;
}

export interface INewSms {
    recipients: IRecipient[];
    templates: ITemplate[];
    defaultTemplateKey: number;
}

export interface INewEmail extends INewSms {
    senders: IEmailSender[];
}

export interface ITemplate extends ICommon {
    name: string;
    subject: string;
    content: string;
    key: number;
}

export interface IRecipient {
    recipientType: IEnumerationItem;
    contactInfo: string;
    language: string;
}

export enum Recipients {
    Unknown = 0,
    Customer = 1,
    Photographer = 2,
    Owner = 3,
    BilderAccount = 4
}

export interface ICreateSmsCommand {
    recipient: string;
    recipientType: Recipients;
    cc: string;
    desiredSendTime: Date;
    body: string;
}

export interface ICreateEmailCommand {
    sender: number;
    subject: string;
    recipient: string;
    cc: string;
    desiredSendTime: Date;
    body: string;
    attachments: number[];
}

export interface IFile {
    reference: any;
    filename: string;
}

export interface ISmsFormState {
    recipient: number;
    recipientType?: IEnumerationItem;
    cc: string;
    desiredSendTime: Date;
    template: number;
    body: string;
}

export interface IEmailFormState extends ISmsFormState {
    sender: number;
    subject: string;
}

export interface IAttachment {
    id: number;
    reference: string;
    filename: string;
}

export interface IAttachments {
    fileName: string;
    warmStorageReference: string;
}

export interface ITotalKeyPickupAddress {
    keyPickupAddress?: string;
    keyPickupZip?: string;
    keyPickupCity?: string;
}

export interface IEditOrderRowViewModel {
    currentOrderRow: IOrderRow;
    possibleProductsForUpdate: IProduct[];
    totalKeyPickupAddress?: ITotalKeyPickupAddress;
}
